import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Trash2, Check, X, Plus, Mail, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  published: "bg-green-100 text-green-800",
  pending: "bg-amber-100 text-amber-800",
  invited: "bg-blue-100 text-blue-800",
  rejected: "bg-red-100 text-red-800",
};

const StarInput = ({ value, onChange }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={`admin-star-${n}`}
        onClick={() => onChange(n)}
        aria-label={`${n} stars`}
      >
        <Star
          className={`w-5 h-5 ${
            n <= value ? "fill-[#C86D51] text-[#C86D51]" : "text-[#0D253A]/25"
          }`}
        />
      </button>
    ))}
  </div>
);

const emptyDraft = {
  guest_name: "",
  guest_city: "",
  rating: 5,
  title: "",
  text: "",
  stay_period: "",
  lang: "it",
};

const AdminReviews = ({ headers, requests, onRefreshRequests }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, { headers });
      setReviews(data.reviews || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento recensioni");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers["X-Admin-Code"]]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNew = () => {
    setEditing(null);
    setDraft(emptyDraft);
    setShowForm(true);
  };

  const startEdit = (r) => {
    setEditing(r.id);
    setDraft({
      guest_name: r.guest_name || "",
      guest_city: r.guest_city || "",
      rating: r.rating || 5,
      title: r.title || "",
      text: r.text || "",
      stay_period: r.stay_period || "",
      lang: r.lang || "it",
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!draft.guest_name || draft.text.length < 10) {
      toast.error("Nome e testo (min 10 caratteri) obbligatori");
      return;
    }
    try {
      if (editing) {
        await axios.patch(`${API}/admin/reviews/${editing}`, draft, { headers });
        toast.success("Recensione aggiornata");
      } else {
        await axios.post(`${API}/admin/reviews`, draft, { headers });
        toast.success("Recensione pubblicata");
      }
      setShowForm(false);
      setEditing(null);
      setDraft(emptyDraft);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}`, { status }, { headers });
      toast.success(status === "published" ? "Pubblicata" : "Aggiornata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa recensione?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers });
      toast.success("Recensione eliminata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const invite = async (requestId) => {
    try {
      await axios.post(`${API}/admin/reviews/invite/${requestId}`, {}, { headers });
      toast.success("Invito inviato all'ospite");
      load();
      if (onRefreshRequests) onRefreshRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl bg-white border border-[#0D253A]/15 text-sm focus:border-[#C86D51] focus:outline-none";

  const paidWithoutInvite = (requests || []).filter(
    (r) => r.status === "pagata" && !r.review_invite_sent_at
  );

  return (
    <div data-testid="studio-reviews" className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#5C6773]">
          {loading ? "Caricamento…" : `${reviews.length} recensioni totali`}
        </p>
        <button
          data-testid="reviews-new-button"
          onClick={startNew}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C86D51] text-white text-xs font-semibold rounded-full hover:bg-[#0D253A] transition-colors"
        >
          <Plus className="w-4 h-4" /> Nuova recensione
        </button>
      </div>

      {/* Inline form */}
      {showForm && (
        <div
          data-testid="reviews-form"
          className="bg-white rounded-2xl border border-[#C86D51]/40 p-6 flex flex-col gap-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">
                Nome ospite *
              </label>
              <input
                data-testid="reviews-name-input"
                className={inputCls}
                value={draft.guest_name}
                onChange={(e) => setDraft({ ...draft, guest_name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">
                Città
              </label>
              <input
                className={inputCls}
                value={draft.guest_city}
                onChange={(e) => setDraft({ ...draft, guest_city: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">
                Voto
              </label>
              <StarInput value={draft.rating} onChange={(v) => setDraft({ ...draft, rating: v })} />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">
                Periodo soggiorno
              </label>
              <input
                className={inputCls}
                placeholder="Es. Agosto 2025"
                value={draft.stay_period}
                onChange={(e) => setDraft({ ...draft, stay_period: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">
              Titolo
            </label>
            <input
              className={inputCls}
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">
              Testo *
            </label>
            <textarea
              data-testid="reviews-text-input"
              rows={4}
              className={`${inputCls} resize-none`}
              value={draft.text}
              onChange={(e) => setDraft({ ...draft, text: e.target.value })}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {["it", "en"].map((l) => (
              <button
                key={l}
                onClick={() => setDraft({ ...draft, lang: l })}
                className={`px-4 py-2 text-xs font-semibold uppercase rounded-full border transition-colors ${
                  draft.lang === l
                    ? "bg-[#0D253A] text-[#F8F5F0] border-[#0D253A]"
                    : "border-[#0D253A]/20 text-[#0D253A]/70"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowForm(false);
                setEditing(null);
              }}
              className="px-5 py-2.5 border border-[#0D253A]/20 text-[#0D253A] text-xs font-semibold rounded-full hover:border-[#0D253A]/40 transition-colors"
            >
              Annulla
            </button>
            <button
              data-testid="reviews-save-button"
              onClick={save}
              className="px-6 py-2.5 bg-[#0D253A] text-white text-xs font-semibold rounded-full hover:bg-[#C86D51] transition-colors"
            >
              {editing ? "Aggiorna" : "Pubblica"}
            </button>
          </div>
        </div>
      )}

      {/* Manual invite section */}
      {paidWithoutInvite.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#0D253A]/10 p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#C86D51] mb-3">
            Invia invito recensione manualmente
          </p>
          <div className="flex flex-col gap-2">
            {paidWithoutInvite.map((r) => (
              <div
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-3 text-sm py-2 border-b border-[#0D253A]/5 last:border-0"
              >
                <span>
                  <strong>{r.name}</strong> · {r.check_in} → {r.check_out}
                </span>
                <button
                  data-testid={`invite-${r.id.slice(0, 8)}`}
                  onClick={() => invite(r.id)}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 border border-[#C86D51] text-[#C86D51] text-xs font-semibold rounded-full hover:bg-[#C86D51] hover:text-white transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" /> Invita
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviews list */}
      <div className="flex flex-col gap-4">
        {reviews.map((r) => (
          <div
            key={r.id}
            data-testid={`admin-review-${r.id.slice(0, 8)}`}
            className="bg-white rounded-2xl border border-[#0D253A]/10 p-6"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="font-display text-lg text-[#0D253A]">{r.guest_name}</p>
                  {r.rating > 0 && (
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`w-3.5 h-3.5 ${
                            n <= r.rating ? "fill-[#C86D51] text-[#C86D51]" : "text-[#0D253A]/20"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                  <span
                    className={`px-2.5 py-0.5 text-[10px] font-semibold uppercase rounded-full ${
                      STATUS_STYLES[r.status] || "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {r.status}
                  </span>
                  <span className="text-[10px] font-mono-alt text-[#0D253A]/40 uppercase">
                    {r.source} · {r.lang}
                  </span>
                </div>
                {r.title && <p className="mt-2 font-semibold text-[#0D253A]">{r.title}</p>}
                {r.text && (
                  <p className="mt-1 text-sm text-[#333D47] whitespace-pre-line">{r.text}</p>
                )}
                <p className="mt-2 text-xs text-[#5C6773]">
                  {[r.guest_city, r.stay_period].filter(Boolean).join(" · ")}
                </p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                {r.status === "pending" && (
                  <button
                    onClick={() => setStatus(r.id, "published")}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-full hover:bg-green-700 transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" /> Pubblica
                  </button>
                )}
                {(r.status === "pending" || r.status === "published") && (
                  <button
                    onClick={() => setStatus(r.id, "rejected")}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#0D253A]/20 text-[#0D253A]/70 text-xs font-semibold rounded-full hover:border-red-500 hover:text-red-600 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> Nascondi
                  </button>
                )}
                {r.status === "rejected" && (
                  <button
                    onClick={() => setStatus(r.id, "published")}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#0D253A]/20 text-[#0D253A]/70 text-xs font-semibold rounded-full hover:border-green-500 hover:text-green-600 transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" /> Ripristina
                  </button>
                )}
                <div className="flex gap-2">
                  {r.status !== "invited" && r.text && (
                    <button
                      onClick={() => startEdit(r)}
                      className="p-1.5 rounded-full border border-[#0D253A]/15 text-[#0D253A]/70 hover:text-[#C86D51] hover:border-[#C86D51] transition-colors"
                      aria-label="Modifica"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    data-testid={`review-delete-${r.id.slice(0, 8)}`}
                    onClick={() => remove(r.id)}
                    className="p-1.5 rounded-full border border-[#0D253A]/15 text-[#0D253A]/70 hover:text-red-600 hover:border-red-500 transition-colors"
                    aria-label="Elimina"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {reviews.length === 0 && !loading && (
          <p className="text-sm text-[#5C6773] italic">Nessuna recensione ancora.</p>
        )}
      </div>
    </div>
  );
};

export default AdminReviews;
